import { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Badge,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Snackbar,
  Alert,
  CircularProgress,
  Stack
} from "@mui/material";
import axios from "axios";
import { useTutorAuth } from "../../context/tutorAuthContext";

import UpcomingSessions from "../../components/tutor/schedule/UpcomingSessions";
import AllSessions from "../../components/tutor/schedule/AllSessions";
import CompletedSessions from "../../components/tutor/schedule/CompletedSessions";

const BASE_URL = process.env.REACT_APP_BASE_URL;

export default function Schedule() {
  const { tutor } = useTutorAuth();
  const tutorId = tutor?.id;

  const [tabValue, setTabValue] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [students, setStudents] = useState([]);
  const [allSchedules, setAllSchedules] = useState([]);

  const [formData, setFormData] = useState({
    studentId: "",
    date: "",
    time: ""
  });

  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingScheduleId, setEditingScheduleId] = useState(null);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success"
  });

  /* -------------------------
     SNACKBAR
  -------------------------- */

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const closeSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  /* -------------------------
     FETCH STUDENTS
  -------------------------- */

  const fetchEnrolledStudents = useCallback(async () => {
    if (!tutorId) return;

    try {
      const res = await axios.get(
        `${BASE_URL}/api/tutors/enrolled-students/${tutorId}`
      );
      setStudents(res.data || []);
    } catch (error) {
      showSnackbar("Failed to fetch students", "error");
    }
  }, [tutorId]);

  /* -------------------------
     FETCH SCHEDULES
  -------------------------- */

  const fetchSchedules = useCallback(async () => {
    if (!tutorId) return;

    try {
      const res = await axios.get(
        `${BASE_URL}/api/tutors/schedules/${tutorId}`
      );
      setAllSchedules(res.data || []);
    } catch (error) {
      showSnackbar("Failed to fetch schedules", "error");
    }
  }, [tutorId]);

  useEffect(() => {
    if (!tutorId) return;

    fetchEnrolledStudents();
    fetchSchedules();
  }, [tutorId, fetchEnrolledStudents, fetchSchedules]);

  /* -------------------------
     FILTER SESSIONS
  -------------------------- */

  const upcoming = allSchedules.filter((s) => s.is_completed === 0);
  const completed = allSchedules.filter((s) => s.is_completed === 1);

  /* -------------------------
     FORM
  -------------------------- */

  const handleFormChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const resetForm = () => {
    setFormData({ studentId: "", date: "", time: "" });
    setEditMode(false);
    setEditingScheduleId(null);
  };

  const closeModal = () => {
    setOpenModal(false);
    resetForm();
  };

  /* -------------------------
     SAVE SCHEDULE
  -------------------------- */

  const handleSaveSchedule = async () => {
    const { studentId, date, time } = formData;

    if (!studentId || !date || !time) {
      showSnackbar("Please fill all fields", "error");
      return;
    }

    const payload = {
      enrollment_id: studentId,
      tutor_id: tutorId,
      date,
      time
    };

    try {
      setLoading(true);

      const url = editMode
        ? `${BASE_URL}/api/tutors/schedule/${editingScheduleId}`
        : `${BASE_URL}/api/tutors/schedule`;

      const method = editMode ? "put" : "post";

      await axios[method](url, payload);

      showSnackbar(
        `Schedule ${editMode ? "updated" : "added"} successfully`
      );

      closeModal();
      fetchSchedules();
    } catch (error) {
      showSnackbar("Failed to save schedule", "error");
    } finally {
      setLoading(false);
    }
  };

  /* -------------------------
     COMPLETE SESSION
  -------------------------- */

  const handleMarkCompleted = async (id) => {
    try {
      await axios.put(`${BASE_URL}/api/tutors/schedule/${id}/complete`);
      showSnackbar("Session marked as completed");
      fetchSchedules();
    } catch {
      showSnackbar("Failed to mark session completed", "error");
    }
  };

  /* -------------------------
     EDIT SESSION
  -------------------------- */

  const handleEdit = (session) => {
    setFormData({
      studentId: session.enrollment_id,
      date: session.date,
      time: session.time
    });

    setEditingScheduleId(session.id);
    setEditMode(true);
    setOpenModal(true);
  };

  /* -------------------------
     UI
  -------------------------- */

  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        maxWidth: 1100,
        mx: "auto"
      }}
    >
      {/* HEADER */}

      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "center" }}
        spacing={2}
        mb={3}
      >
        <Typography
          variant="h5"
          fontWeight="bold"
          sx={{
            fontSize: { xs: "1.25rem", sm: "1.5rem" },
            textAlign: { xs: "center", sm: "left" }
          }}
        >
          My Schedule
        </Typography>

        <Button
          variant="contained"
          onClick={() => setOpenModal(true)}
          fullWidth={false}
          sx={{
            width: { xs: "100%", sm: "auto" }
          }}
        >
          Add Schedule
        </Button>
      </Stack>

      {/* TABS */}

      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={(e, v) => setTabValue(v)}
          variant="fullWidth"
        >
          <Tab
            label={
              <Badge badgeContent={upcoming.length} color="primary">
                Upcoming
              </Badge>
            }
          />

          <Tab label="All Sessions" />

          <Tab label="Completed" />
        </Tabs>
      </Paper>

      {/* TAB CONTENT */}

      {tabValue === 0 && (
        <UpcomingSessions
          sessions={upcoming}
          onMarkCompleted={handleMarkCompleted}
          onEdit={handleEdit}
        />
      )}

      {tabValue === 1 && <AllSessions sessions={allSchedules} />}

      {tabValue === 2 && <CompletedSessions sessions={completed} />}

      {/* MODAL */}

      <Dialog
        open={openModal}
        onClose={closeModal}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {editMode ? "Edit Schedule" : "Schedule a Class"}
        </DialogTitle>

        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            mt: 1
          }}
        >
          <TextField
            select
            label="Select Student"
            name="studentId"
            value={formData.studentId}
            onChange={handleFormChange}
            fullWidth
          >
            {students.map((s) => (
              <MenuItem key={s.enrollment_id} value={s.enrollment_id}>
                {s.student_name} — {s.course_name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Date"
            type="date"
            name="date"
            value={formData.date}
            onChange={handleFormChange}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />

          <TextField
            label="Time"
            type="time"
            name="time"
            value={formData.time}
            onChange={handleFormChange}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={closeModal}>Cancel</Button>

          <Button
            variant="contained"
            onClick={handleSaveSchedule}
            disabled={loading}
            startIcon={
              loading ? <CircularProgress size={18} /> : null
            }
          >
            {editMode ? "Update Schedule" : "Add Schedule"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* SNACKBAR */}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity={snackbar.severity} onClose={closeSnackbar}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}